import React, { useState } from "react";
import axios from "axios";
import ImageUploader from "./ImageUploader";
import ImageSelector from "./ImageSelector";

const AnnouncementComposer = ({ existingAnnouncement = null, onClose, onRefresh, onCancel}) => {
    const [metadata, setMetadata] = useState({
        title: existingAnnouncement?.title || "",
        description: existingAnnouncement?.description || "",
        imageUrl: existingAnnouncement?.imageUrl || "",
        linkText: existingAnnouncement?.linkText || "",
        linkUrl: existingAnnouncement?.linkUrl || "",
        scheduleDate: existingAnnouncement?.scheduleDate || "",
        expireDate: existingAnnouncement?.expireDate || "",
        enabled: existingAnnouncement?.enabled || false,
    });

    const [availableImages, setAvailableImages] = useState([]);
    const [showImageSelector, setShowImageSelector] = useState(false);

    const handleSave = async () => {
        const announcement = { ...metadata };

        try {
            const token = localStorage.getItem("token");

            if (existingAnnouncement?._id) {
                // Update existing announcement
                await axios.put(`/api/announcements/${existingAnnouncement._id}`, announcement, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
            } else {
                // Create new announcement
                await axios.post(`/api/announcements`, announcement, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
            }

            onRefresh(); // Refresh the list after save
            onClose(); // Close the composer
        } catch (error) {
            console.error("Error saving announcement:", error);
            alert("Failed to save announcement. Please try again.");
        }
    };

    const handleImageUpload = async (file) => {
        const formData = new FormData();
        formData.append("image", file);

        try {
            const token = localStorage.getItem("token");
            const response = await axios.post("/api/images/upload-image", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });
            setMetadata({ ...metadata, imageUrl: response.data.path });
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image. Please try again.");
        }
    };

    const fetchAvailableImages = async (subfolder = "") => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get("/api/images/list-images", {
                params: { subfolder },
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailableImages(response.data.images);
            setShowImageSelector(true);
        } catch (error) {
            console.error("Error fetching images:", error);
        }
    };

    const handleImageSelection = (imagePath) => {
        setMetadata({ ...metadata, imageUrl: imagePath });
        setShowImageSelector(false);
    };

    return (
        <div className="announcement-composer container mx-auto p-6">
            <h2 className="text-2xl font-bold mb-4">
                {existingAnnouncement ? "Edit Announcement" : "Create Announcement"}
            </h2>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Form Section */}
                <div className="form-section flex-1">
                    <label>
                        Title
                        <input
                            type="text"
                            placeholder="Title"
                            value={metadata.title}
                            onChange={(e) => setMetadata({...metadata, title: e.target.value})}
                            className="input-title w-full px-4 py-2 border rounded mb-4"
                        />
                    </label>
                    <label>
                        Description
                        <textarea
                            placeholder="Description"
                            value={metadata.description}
                            onChange={(e) => setMetadata({...metadata, description: e.target.value})}
                            className="w-full px-4 py-2 border rounded mb-4"
                        ></textarea>
                    </label>
                    <hr/>
                    <br/>
                    <label>
                        Image Upload
                        <ImageUploader
                            onUpload={(path) => handleImageSelection(path)} // Handle the uploaded image
                        />
                    </label>
                    <br/>
                    <div>
                        <ImageSelector
                            onSelect={(path) => {
                                handleImageSelection(path); // Handle the selected image
                                setShowImageSelector(false); // Close the image selector
                            }}
                        />
                    </div>
                    <br/>
                    <hr/>
                    <br/>
                    <label>
                        Link Text
                        <input
                            type="text"
                            placeholder="Link Text"
                            value={metadata.linkText}
                            onChange={(e) => setMetadata({...metadata, linkText: e.target.value})}
                            className="w-full px-4 py-2 border rounded mb-4"
                        />
                    </label>
                    <label>
                        Link URL
                        <input
                            type="text"
                            placeholder="Link URL"
                        value={metadata.linkUrl}
                        onChange={(e) => setMetadata({...metadata, linkUrl: e.target.value})}
                        className="w-full px-4 py-2 border rounded mb-4"
                    />
                </label>
                <label>
                    Schedule Date
                    <input
                        type="datetime-local"
                        value={
                            metadata.scheduleDate
                                ? new Date(metadata.scheduleDate).toISOString().slice(0, 16)
                                : ""
                        }
                        onChange={(e) =>
                            setMetadata({...metadata, scheduleDate: new Date(e.target.value).toISOString()})
                        }
                        className="w-full px-4 py-2 border rounded mb-4"
                    />
                </label>
                <label>
                    Expiration Date
                    <input
                        type="datetime-local"
                        value={
                            metadata.expireDate
                                ? new Date(metadata.expireDate).toISOString().slice(0, 16)
                                : ""
                        }
                        onChange={(e) =>
                            setMetadata({...metadata, expireDate: new Date(e.target.value).toISOString()})
                        }
                        className="w-full px-4 py-2 border rounded mb-4"
                    />
                </label>
                <label>
                    Enabled
                    <input
                        type="checkbox"
                        checked={metadata.enabled}
                        onChange={(e) => setMetadata({...metadata, enabled: e.target.checked})}
                        className="mr-2"
                    />
                    Enabled
                </label>
            </div>

            {/* Preview Section */}
            <div className="preview-section flex-1 p-4 border rounded bg-gray-100">
                <h3 className="font-bold">{metadata.title || "Preview Title"}</h3>
                <p>{metadata.description || "Preview description"}</p>
                {metadata.imageUrl && (
                    <img src={metadata.imageUrl} alt="Preview" className="rounded mt-4"/>
                )}
                {metadata.linkUrl && (
                    <a
                        href={metadata.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline mt-2 inline-block"
                    >
                        {metadata.linkText}
                    </a>
                )}
                <p className="text-sm mt-2">
                    <strong>Starts:</strong>{" "}
                    {metadata.scheduleDate
                        ? new Date(metadata.scheduleDate).toLocaleString()
                        : "Not scheduled"}
                </p>
                <p className="text-sm">
                    <strong>Expires:</strong>{" "}
                    {metadata.expireDate ? new Date(metadata.expireDate).toLocaleString() : "No expiration"}
                </p>
                <p className="text-sm">
                    <strong>Status:</strong> {metadata.enabled ? "Enabled" : "Disabled"}
                </p>
            </div>
        </div>

            <div className="mt-6">
                <button onClick={handleSave} className="bg-green-500 text-white px-6 py-3 rounded">
                    Save Announcement
                </button>
                <button
                    onClick={onCancel}
                    className="bg-gray-500 text-white px-4 py-2 rounded ml-4"
                >
                    Cancel
                </button>
            </div>
        </div>
    )
        ;
};

export default AnnouncementComposer;
